/**
 * ToolPalette — mode-aware left-edge tool strip.
 *
 * A vertical strip that appears along the left edge of the viewport,
 * populated by the current editor mode with operators, brush tools,
 * and auxiliary slider controls.
 *
 * Features:
 * - **Drag handle** at the top — slides the strip up and down the edge.
 *   Position is persisted to localStorage.
 * - **Vertical overflow** — scrolls when the palette exceeds available
 *   height, mirroring the header bar's horizontal scroll behavior.
 * - **Mode-specific content** — each workspace mode defines what appears.
 *   Hidden entirely in Object, Reference, Correctives, and Render modes.
 * - **Operator buttons** execute operators from the registry, updating
 *   enabled/disabled state based on the live selection context.
 * - **Brush tools** select the active brush in Sculpt/Sketch modes.
 * - **Slider groups** control brush size/strength/opacity, reflecting
 *   whatever brush is currently active.
 */

import {
  ArrowBigUp,
  Command,
  Lock,
  Minus,
  Pencil,
  Plus,
  Pointer,
  Spline,
  Video,
} from "lucide-react";
import type { ReactNode } from "react";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { getShortcutLabel } from "../app/keymap.ts";
import { useStore } from "../app/store.ts";
import type { ViewAxis, ViewSign, WorkspaceMode } from "../app/storeTypes.ts";
import { useOperatorContext } from "../app/useOperatorContext.ts";
import { isOperatorActiveFromCommand } from "../modeling/operatorParams.ts";
import {
  OPERATORS,
  type Operator,
  type OperatorContext,
} from "../modeling/operators.ts";
import type { CurveHandleType, EditGranularity } from "../modeling/types.ts";
import { toggleAxisView, VIEW_AXIS_OPTIONS } from "../viewport/cameraViews.ts";
import { BrushCursorPopover } from "./BrushCursorPopover.tsx";
import { ColorWheel } from "./ColorWheel.tsx";
import { ControlGroupSeparator } from "./ControlGroupSeparator.tsx";
import { EDGE_PALETTE_WIDTH, EdgePalette } from "./EdgePalette.tsx";
import {
  BrushEraseIcon,
  EyeClosedIcon,
  EyeOpenIcon,
  ProjectIcon,
  SketchPencilIcon,
} from "./Icons.tsx";
import {
  AltIcon,
  BrushHardnessIcon,
  BrushOpacityIcon,
  BrushSizeIcon,
  BrushStrengthIcon,
  EdgeModeIcon,
  FaceModeIcon,
  HandleAlignedIcon,
  HandleAutoIcon,
  HandleFreeIcon,
  HandleVectorIcon,
  PenPressureIcon,
  VertexModeIcon,
  ViewAxisIcon,
  XRayIcon,
} from "./icons3d.tsx";
import { SCULPT_BRUSHES, SKETCH_TOOLS } from "./ModeSwitcher.tsx";
import { Popover } from "./Popover.tsx";
import { ratioToValue, valueToRatio } from "./Slider.tsx";
import {
  COLOR_ACCENT,
  COLOR_AXIS_X,
  COLOR_AXIS_Y,
  COLOR_AXIS_Z,
  COLOR_BORDER,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_TINY,
  RADIUS_SM,
  TOOL_FLYOUT_ICON_SIZE,
  TOOL_ICON_SIZE,
} from "./tokens.ts";
import { Tool } from "./tool/Tool.tsx";
import type { ToolVariant } from "./tool/ToolFlyout.tsx";
import { ToolSurface } from "./tool/ToolSurface.tsx";
import { useVirtualModifiers } from "./virtualModifiers.ts";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Width of the palette strip. */
const STRIP_WIDTH = EDGE_PALETTE_WIDTH;
const TOOL_PALETTE_STORAGE_KEY = "poseref-tool-palette-offset";

// ---------------------------------------------------------------------------
// Modes where the palette is visible
// ---------------------------------------------------------------------------

const VISIBLE_MODES: ReadonlySet<WorkspaceMode> = new Set([
  "object",
  "edit",
  "pose",
  "sculpt",
  "sketch",
  "inking",
]);

// ---------------------------------------------------------------------------
// ToolPalette
// ---------------------------------------------------------------------------

export function ToolPalette() {
  const workspaceMode = useStore((s) => s.workspaceMode);
  if (!VISIBLE_MODES.has(workspaceMode)) return null;
  return <ToolPaletteInner mode={workspaceMode} />;
}

function ToolPaletteInner({ mode }: { mode: WorkspaceMode }) {
  return (
    <ToolSurface edge="left">
      <EdgePalette
        id="tool-palette"
        order={0}
        anchor="top"
        storageKey={TOOL_PALETTE_STORAGE_KEY}
      >
        <PaletteContent mode={mode} />
        <PaletteSep />
        <ViewAxisControl />
        <div style={{ height: 6, flexShrink: 0 }} />
      </EdgePalette>
    </ToolSurface>
  );
}

// ---------------------------------------------------------------------------
// PaletteContent — delegates to mode-specific sub-components
// ---------------------------------------------------------------------------

function PaletteContent({ mode }: { mode: WorkspaceMode }) {
  switch (mode) {
    case "object":
      return <ObjectPaletteContent />;
    case "edit":
      return <EditPaletteContent />;
    case "pose":
      return <PosePaletteContent />;
    case "sculpt":
      return <SculptPaletteContent />;
    case "sketch":
      return <SketchPaletteContent />;
    case "inking":
      return <InkingPaletteContent />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Object mode: x-ray toggle + modifier keys + axis constraint
// ---------------------------------------------------------------------------

/** Operator IDs surfaced in the Object mode palette. */
const OBJECT_OPERATOR_IDS = [
  "surface_box",
  "surface_cylinder",
  "surface_poly",
  "surface_inset",
] as const;

function ObjectPaletteContent() {
  const objectXray = useStore((s) => s.objectXray);
  const setObjectXray = useStore((s) => s.setObjectXray);
  const ctx = useOperatorContext();

  const operators = useMemo(
    () =>
      OBJECT_OPERATOR_IDS.map(
        (id) => OPERATORS.find((op) => op.id === id)!,
      ).filter(Boolean),
    [],
  );

  return (
    <>
      <Tool
        icon={<XRayIcon size={TOOL_ICON_SIZE} />}
        tip={`X-Ray${getShortcutLabel("toggleObjectXray") ? ` (${getShortcutLabel("toggleObjectXray")})` : ""}`}
        active={objectXray}
        onActivate={() => setObjectXray(!objectXray)}
      />

      <PaletteSep />

      {operators.map((op) => (
        <OperatorTool key={op.id} operator={op} ctx={ctx} />
      ))}

      <PaletteSep />

      <ModifierControls />
    </>
  );
}

// ---------------------------------------------------------------------------
// ModifierControls — Shift/Ctrl toggles + axis-constraint popover.
// Shared between object and edit mode palettes.
// ---------------------------------------------------------------------------

const AXIS_COLORS: Record<string, string> = {
  x: COLOR_AXIS_X,
  y: COLOR_AXIS_Y,
  z: COLOR_AXIS_Z,
};

function ModifierControls() {
  const shiftActive = useVirtualModifiers((s) =>
    s.activeModifiers.has("shift"),
  );
  const altActive = useVirtualModifiers((s) => s.activeModifiers.has("alt"));
  const cmdActive = useVirtualModifiers((s) => s.activeModifiers.has("cmd"));
  const toggleModifier = useVirtualModifiers((s) => s.toggleModifier);

  const currentAxis = useStore((s) => s.commandBuffer?.axis ?? null);
  const pendingAxis = useStore((s) => s.pendingAxis);
  const setCommandAxis = useStore((s) => s.setCommandAxis);
  const hasCommand = useStore((s) => s.commandBuffer != null);
  const activeAxis = hasCommand ? currentAxis : pendingAxis;

  const axisVariants: ToolVariant[] = [
    ...(["x", "y", "z"] as const).map((axis) => ({
      key: axis,
      icon: (
        <span style={{ fontWeight: 700, color: AXIS_COLORS[axis] }}>
          {axis.toUpperCase()}
        </span>
      ),
      label: `${axis.toUpperCase()} Axis`,
      shortcut: axis.toUpperCase(),
      selected: activeAxis === axis,
      color: activeAxis === axis ? AXIS_COLORS[axis] : undefined,
      iconColor: AXIS_COLORS[axis],
      group: "axis",
      onActivate: () => setCommandAxis(activeAxis === axis ? null : axis),
    })),
    ...(activeAxis != null
      ? [
          {
            key: "clear",
            icon: <span />,
            label: "Clear",
            color: COLOR_TEXT_DIM,
            iconColor: COLOR_TEXT_DIM,
            group: "clear",
            onActivate: () => setCommandAxis(null),
          } as ToolVariant,
        ]
      : []),
  ];

  return (
    <>
      <Tool
        icon={<ArrowBigUp size={TOOL_ICON_SIZE} />}
        tip={shiftActive ? "Release Shift" : "Hold Shift (additive select)"}
        active={shiftActive}
        onActivate={() => toggleModifier("shift")}
      />

      <Tool
        icon={<AltIcon size={TOOL_ICON_SIZE} />}
        tip={altActive ? "Release Alt" : "Hold Alt (loop select)"}
        active={altActive}
        onActivate={() => toggleModifier("alt")}
      />

      <Tool
        icon={<Command size={TOOL_ICON_SIZE} />}
        tip={cmdActive ? "Release Ctrl" : "Hold Ctrl"}
        active={cmdActive}
        onActivate={() => toggleModifier("cmd")}
      />

      <PaletteSep />

      <Tool
        icon={
          activeAxis ? (
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: AXIS_COLORS[activeAxis],
              }}
            >
              {activeAxis.toUpperCase()}
            </span>
          ) : (
            <Lock size={TOOL_ICON_SIZE} />
          )
        }
        tip="Axis constraint"
        active={activeAxis != null}
        variants={axisVariants}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Edit mode: fill, bevel, extrude, inset + vertex/edge/face group
// ---------------------------------------------------------------------------

/** Operator IDs surfaced in the Edit mode palette (fill + extrude handled by tool belts). */
const EDIT_OPERATOR_IDS = [
  "connect_vertex_path",
  "bevel",
  "inset_faces",
  "insert_edge_loop",
  "shrink_fatten",
  "skin_radius",
  "rip",
] as const;

/** Tool belt: fill variants. */
const FILL_BELT_IDS = ["fill", "grid_fill"] as const;

/** Tool belt: extrude variants. */
const EXTRUDE_BELT_IDS = [
  "extrude",
  "extrude_along_normals",
  "extrude_manifold",
  "extrude_individual",
] as const;

function EditPaletteContent() {
  const editMode = useStore((s) => s.editMode);
  const setEditGranularity = useStore((s) => s.setEditGranularity);
  const toggleEditXray = useStore((s) => s.toggleEditXray);
  const ctx = useOperatorContext();

  const operators = useMemo(
    () =>
      EDIT_OPERATOR_IDS.map(
        (id) => OPERATORS.find((op) => op.id === id)!,
      ).filter(Boolean),
    [],
  );

  const isCurve = editMode?.isCurve ?? false;

  if (isCurve) {
    return (
      <>
        <CurveHandleControl />

        <PaletteSep />

        <ModifierControls />
      </>
    );
  }

  return (
    <>
      <GranularityControl
        granularity={editMode?.granularity ?? "vertex"}
        onChange={setEditGranularity}
      />

      {/* X-ray toggle — see through mesh to select occluded elements */}
      <Tool
        icon={<XRayIcon size={TOOL_ICON_SIZE} />}
        tip={`X-Ray${getShortcutLabel("toggleEditXray") ? ` (${getShortcutLabel("toggleEditXray")})` : ""}`}
        active={editMode?.xray ?? false}
        onActivate={toggleEditXray}
      />

      <PaletteSep />

      {/* Fill tool group (fill / grid fill) */}
      <OperatorGroupTool operatorIds={FILL_BELT_IDS} ctx={ctx} />

      {/* Extrude tool group (region / along normals / individual) */}
      <OperatorGroupTool operatorIds={EXTRUDE_BELT_IDS} ctx={ctx} />

      {/* Operator shortcuts */}
      {operators.map((op) => (
        <OperatorTool key={op.id} operator={op} ctx={ctx} />
      ))}

      <PaletteSep />

      <ModifierControls />
    </>
  );
}

// ---------------------------------------------------------------------------
// Curve handle type popover — segmented control for auto/vector/aligned/free
// ---------------------------------------------------------------------------

const HANDLE_OPTIONS: {
  value: CurveHandleType;
  label: string;
  icon: React.ComponentType<{ size?: number | string }>;
}[] = [
  { value: "auto", label: "Auto", icon: HandleAutoIcon },
  { value: "vector", label: "Vector", icon: HandleVectorIcon },
  { value: "aligned", label: "Aligned", icon: HandleAlignedIcon },
  { value: "free", label: "Free", icon: HandleFreeIcon },
];

function CurveHandleControl() {
  const setCurveHandleType = useStore((s) => s.setCurveHandleType);
  const hasSelection = useStore(
    (s) => (s.editMode?.selectedVertices.size ?? 0) > 0,
  );

  const variants: ToolVariant[] = HANDLE_OPTIONS.map((opt) => {
    const Icon = opt.icon;
    return {
      key: opt.value,
      icon: <Icon size={TOOL_FLYOUT_ICON_SIZE} />,
      label: opt.label,
      onActivate: () => setCurveHandleType(opt.value),
    };
  });

  return (
    <Tool
      icon={<Spline size={TOOL_ICON_SIZE} />}
      tip="Curve handle type"
      disabled={!hasSelection}
      variants={variants}
    />
  );
}

// ---------------------------------------------------------------------------
// Pose mode: relax, mirror, pin, lock, drop
// ---------------------------------------------------------------------------

/** Operator IDs for pose mode (excluding lock/pin, which are state toggles). */
const POSE_OPERATOR_IDS = ["relax_bones", "mirror_pose", "drop"] as const;

function PosePaletteContent() {
  const poseMode = useStore((s) => s.poseMode);
  const lockedBones = useStore((s) => s.lockedBones);
  const pinnedBones = useStore((s) => s.pinnedBones);
  const ctx = useOperatorContext();

  const operators = useMemo(
    () =>
      POSE_OPERATOR_IDS.map(
        (id) => OPERATORS.find((op) => op.id === id)!,
      ).filter(Boolean),
    [],
  );

  // Derive lock/pin state from actual bone data.
  const objectId = poseMode?.objectId;
  const selectedBones = poseMode?.selectedBones;
  const hasBones = (selectedBones?.size ?? 0) > 0;

  const allLocked = useMemo(() => {
    if (!objectId || !selectedBones || selectedBones.size === 0) return false;
    const set = lockedBones.get(objectId);
    if (!set) return false;
    for (const b of selectedBones) {
      if (!set.has(b)) return false;
    }
    return true;
  }, [objectId, selectedBones, lockedBones]);

  const allPinned = useMemo(() => {
    if (!objectId || !selectedBones || selectedBones.size === 0) return false;
    const map = pinnedBones.get(objectId);
    if (!map) return false;
    for (const b of selectedBones) {
      if (!map.has(b)) return false;
    }
    return true;
  }, [objectId, selectedBones, pinnedBones]);

  const lockOp = useMemo(
    () => OPERATORS.find((o) => o.id === "lock_joint")!,
    [],
  );
  const pinOp = useMemo(() => OPERATORS.find((o) => o.id === "pin_joint")!, []);

  return (
    <>
      {/* Execute-once operators */}
      {operators.map((op) => (
        <OperatorTool key={op.id} operator={op} ctx={ctx} />
      ))}

      <PaletteSep />

      {/* Pin / Lock — reflect actual bone state. */}
      <Tool
        icon={<pinOp.icon size={TOOL_ICON_SIZE} />}
        tip={
          allPinned
            ? `Unpin joints (${getShortcutLabel("pin_joint")})`
            : `Pin joints (${getShortcutLabel("pin_joint")})`
        }
        active={allPinned}
        disabled={!hasBones}
        onActivate={() => pinOp.execute()}
      />
      <Tool
        icon={<lockOp.icon size={TOOL_ICON_SIZE} />}
        tip={allLocked ? "Unlock joints" : "Lock joints"}
        active={allLocked}
        disabled={!hasBones}
        onActivate={() => lockOp.execute()}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Sculpt mode: brush tools + size/strength sliders
// ---------------------------------------------------------------------------

function DrawInputToggle() {
  const allowNonPenDrawingInput = useStore((s) => s.allowNonPenDrawingInput);
  const setAllowNonPenDrawingInput = useStore(
    (s) => s.setAllowNonPenDrawingInput,
  );

  return (
    <Tool
      icon={<Pointer size={TOOL_ICON_SIZE} />}
      tip={
        allowNonPenDrawingInput ? "Mouse + touch drawing" : "Pen-only drawing"
      }
      active={allowNonPenDrawingInput}
      onActivate={() => setAllowNonPenDrawingInput(!allowNonPenDrawingInput)}
    />
  );
}

function SculptPaletteContent() {
  const sculptMode = useStore((s) => s.sculptMode);
  const setSculptBrush = useStore((s) => s.setSculptBrush);
  const setSculptRadius = useStore((s) => s.setSculptRadius);
  const setSculptStrength = useStore((s) => s.setSculptStrength);
  const setSculptInvert = useStore((s) => s.setSculptInvert);

  const groupRef = useRef<HTMLDivElement>(null);
  const [activeSliders, setActiveSliders] = useState(0);
  const onSliderActiveChange = useCallback((active: boolean) => {
    setActiveSliders((c) => c + (active ? 1 : -1));
  }, []);

  if (!sculptMode) return null;

  return (
    <>
      {/* Brush selector */}
      {SCULPT_BRUSHES.map((b) => (
        <Tool
          key={b.key}
          icon={b.icon}
          tip={`${b.label} (${b.shortcut})`}
          active={sculptMode.brush === b.key}
          onActivate={() => setSculptBrush(b.key)}
        />
      ))}

      <DrawInputToggle />

      <PaletteSep />

      {/* Size & Strength sliders */}
      <PaletteSliderGroup ref={groupRef}>
        <PaletteSlider
          icon={<BrushSizeIcon size={15} />}
          tip="Brush size"
          value={sculptMode.radius}
          min={1}
          max={150}
          step={1}
          onChange={setSculptRadius}
          defaultValue={50}
          midpoint={50}
          onActiveChange={onSliderActiveChange}
        />
        <PaletteSlider
          icon={<BrushStrengthIcon size={15} />}
          tip="Brush strength"
          value={sculptMode.strength}
          min={0}
          max={1}
          step={0.01}
          onChange={setSculptStrength}
          defaultValue={0.5}
          onActiveChange={onSliderActiveChange}
        />
      </PaletteSliderGroup>

      <BrushCursorPopover
        anchorRef={groupRef}
        open={activeSliders > 0}
        radiusPx={sculptMode.radius}
        maxRadiusPx={150}
        intensity={sculptMode.strength}
        hardness={1}
        color={sculptMode.invert ? "#d94a4a" : "#4a90d9"}
      />

      <PaletteSep />

      <SculptDirectionControl
        invert={sculptMode.invert}
        onChange={setSculptInvert}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// SculptDirectionControl — Add/Subtract tool belt
// ---------------------------------------------------------------------------

const SCULPT_DIRECTION_OPTIONS: {
  key: boolean;
  label: string;
  icon: typeof Plus;
}[] = [
  { key: false, label: "Add", icon: Plus },
  { key: true, label: "Subtract", icon: Minus },
];

function SculptDirectionControl({
  invert,
  onChange,
}: {
  invert: boolean;
  onChange: (v: boolean) => void;
}) {
  const active = SCULPT_DIRECTION_OPTIONS.find((o) => o.key === invert)!;
  const ActiveIcon = active.icon;

  const variants: ToolVariant[] = SCULPT_DIRECTION_OPTIONS.map((opt) => {
    const Icon = opt.icon;
    return {
      key: opt.label,
      icon: <Icon size={TOOL_FLYOUT_ICON_SIZE} />,
      label: opt.label,
      selected: opt.key === invert,
      onActivate: () => onChange(opt.key),
    };
  });

  return (
    <Tool
      icon={<ActiveIcon size={TOOL_ICON_SIZE} />}
      tip={active.label}
      variants={variants}
    />
  );
}

// ---------------------------------------------------------------------------
// Sketch mode: brush tools + thickness slider + color
// ---------------------------------------------------------------------------

function SketchProjectionToggle() {
  const sketchMode = useStore((s) => s.sketchMode);
  const setSketchProjection = useStore((s) => s.setSketchProjection);
  if (!sketchMode) return null;
  const projected = sketchMode.projection === "projected";
  return (
    <Tool
      icon={<ProjectIcon size={TOOL_ICON_SIZE} />}
      tip={
        projected
          ? "Project: ON — strokes follow surfaces"
          : "Project: OFF — strokes float 1u from camera"
      }
      active={projected}
      onActivate={() =>
        setSketchProjection(projected ? "screenspace" : "projected")
      }
    />
  );
}

function SketchPaletteContent() {
  const sketchMode = useStore((s) => s.sketchMode);
  const setSketchTool = useStore((s) => s.setSketchTool);
  const setSketchThickness = useStore((s) => s.setSketchThickness);
  const setSketchColor = useStore((s) => s.setSketchColor);

  const groupRef = useRef<HTMLDivElement>(null);
  const [activeSliders, setActiveSliders] = useState(0);
  const onSliderActiveChange = useCallback((active: boolean) => {
    setActiveSliders((c) => c + (active ? 1 : -1));
  }, []);

  if (!sketchMode) return null;

  const isErase = sketchMode.tool === "erase";

  return (
    <>
      {/* Tool selector */}
      {SKETCH_TOOLS.map((b) => (
        <Tool
          key={b.key}
          icon={b.icon}
          tip={`${b.label} (${b.shortcut})`}
          active={sketchMode.tool === b.key}
          onActivate={() => setSketchTool(b.key)}
        />
      ))}

      <SketchProjectionToggle />

      <DrawInputToggle />

      <PaletteSep />

      {/* Thickness slider */}
      <PaletteSliderGroup ref={groupRef}>
        <PaletteSlider
          icon={<BrushSizeIcon size={15} />}
          tip="Stroke thickness"
          value={sketchMode.thickness}
          min={1}
          max={40}
          step={1}
          onChange={setSketchThickness}
          defaultValue={6}
          midpoint={6}
          onActiveChange={onSliderActiveChange}
        />
      </PaletteSliderGroup>

      <BrushCursorPopover
        anchorRef={groupRef}
        open={activeSliders > 0}
        radiusPx={sketchMode.thickness}
        maxRadiusPx={40}
        intensity={1}
        hardness={1}
        color={isErase ? "#d94a4a" : sketchMode.color}
      />

      <PaletteSep />

      {/* Color well — hidden for erase, which has no color. */}
      {!isErase && (
        <PaletteColorWell color={sketchMode.color} onChange={setSketchColor} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Inking mode: enter-edit toggle, pen/eraser, size/opacity/hardness, reference
// ---------------------------------------------------------------------------

const INKING_TOOLS: {
  key: "pen" | "eraser";
  label: string;
  icon: ReactNode;
}[] = [
  { key: "pen", label: "Pen", icon: <SketchPencilIcon /> },
  { key: "eraser", label: "Eraser", icon: <BrushEraseIcon /> },
];

function InkingPaletteContent() {
  const inkingMode = useStore((s) => s.inkingMode);
  const setInkingEditSubMode = useStore((s) => s.setInkingEditSubMode);
  const setInkingTool = useStore((s) => s.setInkingTool);
  const setInkingBrushSize = useStore((s) => s.setInkingBrushSize);
  const setInkingBrushOpacity = useStore((s) => s.setInkingBrushOpacity);
  const setInkingBrushHardness = useStore((s) => s.setInkingBrushHardness);
  const setInkingBrushColor = useStore((s) => s.setInkingBrushColor);
  const setInkingPressureMode = useStore((s) => s.setInkingPressureMode);
  const setInkingShowReference = useStore((s) => s.setInkingShowReference);
  const resetInkingView = useStore((s) => s.resetInkingView);

  const groupRef = useRef<HTMLDivElement>(null);
  const [activeSliders, setActiveSliders] = useState(0);
  const onSliderActiveChange = useCallback((active: boolean) => {
    setActiveSliders((c) => c + (active ? 1 : -1));
  }, []);

  if (!inkingMode) return null;

  const editing = inkingMode.editSubMode === "edit";
  // The edit toggle is only meaningful once the pipeline has produced a
  // raster — drawing into an empty canvas isn't useful, and the page
  // boundary is only fit to the frame after bake. Until then, guide the
  // user toward Generate.
  const canEdit = inkingMode.rasterBuffer != null;
  const isErase = inkingMode.tool === "eraser";

  return (
    <>
      <Tool
        icon={<Pencil size={TOOL_ICON_SIZE} />}
        tip={
          editing
            ? "Exit edit — back to preview"
            : canEdit
              ? "Edit inking"
              : "Generate inking to enable editing"
        }
        active={editing}
        disabled={!canEdit}
        onActivate={() => setInkingEditSubMode(editing ? "view" : "edit")}
      />

      {editing && (
        <>
          <PaletteSep />

          {/* Pen / Eraser */}
          {INKING_TOOLS.map((t) => (
            <Tool
              key={t.key}
              icon={t.icon}
              tip={t.label}
              active={inkingMode.tool === t.key}
              onActivate={() => setInkingTool(t.key)}
            />
          ))}

          <DrawInputToggle />

          <PaletteSep />

          {/* Size, opacity, hardness sliders */}
          <PaletteSliderGroup ref={groupRef}>
            <PaletteSlider
              icon={<BrushSizeIcon size={15} />}
              tip="Brush size"
              value={inkingMode.size}
              min={1}
              max={80}
              step={1}
              onChange={setInkingBrushSize}
              defaultValue={8}
              midpoint={40}
              onActiveChange={onSliderActiveChange}
            />
            <PaletteSlider
              icon={<BrushOpacityIcon size={15} />}
              tip="Opacity"
              value={inkingMode.opacity}
              min={0}
              max={1}
              step={0.01}
              onChange={setInkingBrushOpacity}
              defaultValue={1}
              onActiveChange={onSliderActiveChange}
            />
            <PaletteSlider
              icon={<BrushHardnessIcon size={15} />}
              tip="Hardness"
              value={inkingMode.hardness}
              min={0}
              max={1}
              step={0.01}
              onChange={setInkingBrushHardness}
              defaultValue={0.9}
              onActiveChange={onSliderActiveChange}
            />
          </PaletteSliderGroup>

          <BrushCursorPopover
            anchorRef={groupRef}
            open={activeSliders > 0}
            radiusPx={inkingMode.size}
            maxRadiusPx={80}
            intensity={inkingMode.opacity}
            hardness={inkingMode.hardness}
            color={isErase ? "#d94a4a" : inkingMode.color}
          />

          <PaletteSep />

          {/* Ink color (hidden in eraser mode — nothing to pick) */}
          {!isErase && (
            <>
              <PaletteColorWell
                color={inkingMode.color}
                onChange={setInkingBrushColor}
              />
              <PaletteSep />
            </>
          )}

          {/* Pressure effect */}
          <PressureControl
            pressureMode={inkingMode.pressureMode}
            onChange={setInkingPressureMode}
          />

          <PaletteSep />

          {/* Reference-fade toggle — drops the page back to 0.72 so the 3D
              scene shows through for alignment touch-ups. */}
          <Tool
            icon={
              inkingMode.showReference ? (
                <EyeOpenIcon size={TOOL_ICON_SIZE} />
              ) : (
                <EyeClosedIcon size={TOOL_ICON_SIZE} />
              )
            }
            tip={
              inkingMode.showReference
                ? "Hide 3D reference"
                : "Show 3D reference"
            }
            active={inkingMode.showReference}
            onActivate={() => setInkingShowReference(!inkingMode.showReference)}
          />

          {/* Reset pan/zoom to fit. */}
          <Tool
            icon={<ViewAxisIcon size={TOOL_ICON_SIZE} />}
            tip="Reset page view"
            onActivate={resetInkingView}
          />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// PaletteColorWell — swatch that opens a color picker popover
// ---------------------------------------------------------------------------

function PaletteColorWell({
  color,
  onChange,
}: {
  color: string;
  onChange: (color: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={() => setOpen((p) => !p)}
        title={`Color: ${color}`}
        style={{
          width: 28,
          height: 28,
          borderRadius: RADIUS_SM,
          border: open
            ? `1.5px solid ${COLOR_ACCENT}`
            : `1.5px solid rgba(255,255,255,0.15)`,
          background: color,
          cursor: "pointer",
          padding: 0,
          flexShrink: 0,
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.25)",
          transition: "border-color 0.15s ease",
        }}
      />
      <Popover
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        style={{
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
        }}
      >
        <ColorWheel value={color} onChange={onChange} size={140} />
        <span
          style={{
            fontSize: FONT_SIZE_TINY,
            color: COLOR_TEXT_SUBTLE,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {color.toUpperCase()}
        </span>
      </Popover>
    </>
  );
}

// ---------------------------------------------------------------------------
// PressureControl — pressure effect tool belt
// ---------------------------------------------------------------------------

const PRESSURE_ICON_OPTIONS = [
  { label: "Opacity", value: "opacity" as const, icon: BrushOpacityIcon },
  { label: "Size", value: "size" as const, icon: BrushSizeIcon },
];

function PressureControl({
  pressureMode,
  onChange,
}: {
  pressureMode: "opacity" | "size";
  onChange: (v: "opacity" | "size") => void;
}) {
  const variants: ToolVariant[] = PRESSURE_ICON_OPTIONS.map((opt) => {
    const Icon = opt.icon;
    return {
      key: opt.value,
      icon: <Icon size={TOOL_FLYOUT_ICON_SIZE} />,
      label: opt.label,
      selected: opt.value === pressureMode,
      onActivate: () => onChange(opt.value),
    };
  });

  return (
    <Tool
      icon={<PenPressureIcon size={TOOL_ICON_SIZE} />}
      tip={`Pressure: ${pressureMode}`}
      variants={variants}
    />
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

/** Horizontal separator inside the palette. */
// ---------------------------------------------------------------------------
// ViewAxisControl — axis-aligned view switcher (front/back/left/right/top/bottom)
//
// Rendered at the bottom of every mode's palette. Shares the expandable
// tool-belt pattern (popover + touch drag-select) used by Axis-lock and
// Granularity controls above.
//
// When a view is active the button shows the locked axis letter in its axis
// color; when unlocked, a neutral 3-axis icon. Tapping the active entry
// exits the lock (Blender numpad behavior has no direct analogue for this
// — we treat the active button as a toggle).
// ---------------------------------------------------------------------------

const AXIS_COLOR_MAP: Record<ViewAxis, string> = {
  x: COLOR_AXIS_X,
  y: COLOR_AXIS_Y,
  z: COLOR_AXIS_Z,
};

function ViewAxisControl() {
  const viewLock = useStore((s) => s.viewLock);

  const handleSelect = useCallback((axis: ViewAxis, sign: ViewSign) => {
    toggleAxisView(axis, sign);
  }, []);

  // Active button label: axis letter colored to match the world axis; show
  // sign with a ± prefix only when negative (e.g. "-Y" for front).
  const activeIcon = viewLock ? (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: AXIS_COLOR_MAP[viewLock.axis],
        lineHeight: 1,
        letterSpacing: -0.5,
      }}
    >
      {viewLock.sign < 0 ? "−" : ""}
      {viewLock.axis.toUpperCase()}
    </span>
  ) : (
    <Video size={TOOL_ICON_SIZE} />
  );

  const tip = viewLock
    ? `View: ${VIEW_AXIS_OPTIONS.find((o) => o.axis === viewLock.axis && o.sign === viewLock.sign)?.label ?? ""} — tap to exit`
    : "Axis view";

  const variants: ToolVariant[] = VIEW_AXIS_OPTIONS.map((opt) => {
    const isActive = viewLock?.axis === opt.axis && viewLock?.sign === opt.sign;
    const shortcut =
      getShortcutLabel(`view.axis.${opt.label.toLowerCase()}`) ?? undefined;
    return {
      key: `${opt.axis}${opt.sign}`,
      icon: (
        <span
          style={{
            fontWeight: 700,
            color: AXIS_COLOR_MAP[opt.axis],
          }}
        >
          {opt.sign < 0 ? "−" : "+"}
          {opt.axis.toUpperCase()}
        </span>
      ),
      iconColor: AXIS_COLOR_MAP[opt.axis],
      label: opt.label,
      shortcut,
      selected: isActive,
      onActivate: () => handleSelect(opt.axis, opt.sign),
    };
  });

  return (
    <Tool
      icon={activeIcon}
      tip={tip}
      active={viewLock != null}
      variants={variants}
    />
  );
}

function PaletteSep() {
  return <ControlGroupSeparator />;
}

// ---------------------------------------------------------------------------
// GranularityControl — vertex / edge / face tool belt
// ---------------------------------------------------------------------------

const GRANULARITY_OPTIONS: {
  value: EditGranularity;
  label: string;
  icon: React.ComponentType<{ size?: number | string }>;
}[] = [
  { value: "vertex", label: "Vertex", icon: VertexModeIcon },
  { value: "edge", label: "Edge", icon: EdgeModeIcon },
  { value: "face", label: "Face", icon: FaceModeIcon },
];

function GranularityControl({
  granularity,
  onChange,
}: {
  granularity: EditGranularity;
  onChange: (g: EditGranularity) => void;
}) {
  const current = GRANULARITY_OPTIONS.find((o) => o.value === granularity)!;
  const ActiveIcon = current.icon;

  const variants: ToolVariant[] = GRANULARITY_OPTIONS.map((opt) => {
    const Icon = opt.icon;
    const shortcut = getShortcutLabel(`granularity.${opt.value}`) ?? undefined;
    return {
      key: opt.value,
      icon: <Icon size={TOOL_FLYOUT_ICON_SIZE} />,
      label: opt.label,
      shortcut,
      selected: granularity === opt.value,
      onActivate: () => onChange(opt.value),
    };
  });

  return (
    <Tool
      icon={<ActiveIcon size={TOOL_ICON_SIZE} />}
      tip={`Select: ${current.label}`}
      variants={variants}
    />
  );
}

// Palette-operated tools should arm a viewport control instead of starting an
// immediate mouse-driven drag. Keyboard shortcuts and the Alt+E pie still use
// the operators' normal execute path.
const TOOL_PALETTE_GIZMO_OPERATOR_IDS = new Set([
  "extrude",
  "extrude_along_normals",
  "extrude_individual",
  "extrude_manifold",
  "inset_faces",
  "bevel",
  "shrink_fatten",
  "skin_radius",
  "surface_box",
  "surface_cylinder",
  "surface_poly",
  "surface_inset",
]);

function executeToolPaletteOperator(
  operator: Operator,
  activeOperator: string | null,
  armToolOperator: (id: string | null) => void,
) {
  if (!TOOL_PALETTE_GIZMO_OPERATOR_IDS.has(operator.id)) {
    operator.execute();
    return;
  }
  armToolOperator(activeOperator === operator.id ? null : operator.id);
}

/** Container for vertical slider controls — stacked vertically. */
const PaletteSliderGroup = forwardRef<HTMLDivElement, { children: ReactNode }>(
  function PaletteSliderGroup({ children }, ref) {
    return (
      <div
        ref={ref}
        style={{
          width: STRIP_WIDTH - 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          padding: "4px 2px",
        }}
      >
        {children}
      </div>
    );
  },
);

// ---------------------------------------------------------------------------
// OperatorTool — executes a single operator; sets the anchor point used by
// any downstream UI (transform gizmo). Uses the Tool primitive with a caution
// accent so operator activation reads distinctly from plain toggle buttons.
// ---------------------------------------------------------------------------

function OperatorTool({
  operator,
  ctx,
}: {
  operator: Operator;
  ctx: OperatorContext;
}) {
  const setAnchorPoint = useStore((s) => s.setOperatorAnchorPoint);
  const activeOperator = useStore((s) => s.activeOperator);
  const armToolOperator = useStore((s) => s.armToolOperator);
  const isActive = useStore((s) => {
    if (!operator.toggle) return false;
    if (s.activeOperator === operator.id) return true;
    const cb = s.commandBuffer;
    return cb ? isOperatorActiveFromCommand(operator.id, cb.type) : false;
  });
  const enabled = operator.isEnabled(ctx);
  const Icon = operator.icon;
  const shortcut = getShortcutLabel(operator.id);
  const btnRef = useRef<HTMLButtonElement>(null);

  const handleActivate = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setAnchorPoint({ x: rect.right, y: rect.top });
    executeToolPaletteOperator(operator, activeOperator, armToolOperator);
  }, [operator, setAnchorPoint, activeOperator, armToolOperator]);

  if (!operator.isRelevant(ctx)) return null;

  return (
    <Tool
      ref={btnRef}
      icon={<Icon size={TOOL_ICON_SIZE} />}
      tip={shortcut ? `${operator.label} (${shortcut})` : operator.label}
      active={isActive}
      accent="caution"
      disabled={!enabled}
      onActivate={handleActivate}
    />
  );
}

// ---------------------------------------------------------------------------
// OperatorGroupTool — a tool group of related operators (Fill / GridFill;
// Extrude / AlongNormals / Individual / Manifold). The primary button shows
// the currently-active variant's icon; long-press / drag / click opens the
// flyout. Activation anchors downstream UI to the primary button's rect so
// the gizmo appears next to the palette strip regardless of which variant
// fires.
// ---------------------------------------------------------------------------

function OperatorGroupTool({
  operatorIds,
  ctx,
}: {
  operatorIds: readonly string[];
  ctx: OperatorContext;
}) {
  const setAnchorPoint = useStore((s) => s.setOperatorAnchorPoint);
  const activeOperator = useStore((s) => s.activeOperator);
  const commandBuffer = useStore((s) => s.commandBuffer);
  const armToolOperator = useStore((s) => s.armToolOperator);
  const btnRef = useRef<HTMLButtonElement>(null);

  const ops = useMemo(
    () =>
      operatorIds
        .map((id) => OPERATORS.find((op) => op.id === id)!)
        .filter(Boolean),
    [operatorIds],
  );
  const relevantOps = useMemo(
    () => ops.filter((op) => op.isRelevant(ctx)),
    [ops, ctx],
  );

  const activeOp = useMemo(
    () =>
      relevantOps.find(
        (op) =>
          op.toggle &&
          (activeOperator === op.id ||
            (commandBuffer
              ? isOperatorActiveFromCommand(op.id, commandBuffer.type)
              : false)),
      ) ?? null,
    [relevantOps, activeOperator, commandBuffer],
  );

  const runOperator = useCallback(
    (op: Operator) => {
      const rect = btnRef.current?.getBoundingClientRect();
      if (rect) setAnchorPoint({ x: rect.right, y: rect.top });
      executeToolPaletteOperator(op, activeOperator, armToolOperator);
    },
    [setAnchorPoint, activeOperator, armToolOperator],
  );

  const primaryOp =
    activeOp ?? relevantOps.find((op) => op.isEnabled(ctx)) ?? relevantOps[0];
  if (!primaryOp) return null;

  const anyEnabled = relevantOps.some((op) => op.isEnabled(ctx));
  const PrimaryIcon = primaryOp.icon;

  const variants: ToolVariant[] = relevantOps.map((op) => {
    const Icon = op.icon;
    const shortcut = getShortcutLabel(op.id) ?? undefined;
    return {
      key: op.id,
      icon: <Icon size={TOOL_FLYOUT_ICON_SIZE} />,
      label: op.label,
      shortcut,
      disabled: !op.isEnabled(ctx),
      onActivate: () => runOperator(op),
    };
  });

  return (
    <Tool
      ref={btnRef}
      icon={<PrimaryIcon size={TOOL_ICON_SIZE} />}
      tip={primaryOp.label}
      active={activeOp != null}
      disabled={!anyEnabled}
      variants={variants}
    />
  );
}

// ---------------------------------------------------------------------------
// PaletteSlider — vertical slider with label above and value below
// ---------------------------------------------------------------------------

/** Track height for the vertical slider. */
const V_SLIDER_TRACK_H = 64;

function PaletteSlider({
  icon,
  tip,
  value,
  min,
  max,
  step,
  onChange,
  defaultValue,
  onActiveChange,
  midpoint,
}: {
  icon: ReactNode;
  tip: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  defaultValue?: number;
  /** Called when hover or drag state changes. */
  onActiveChange?: (active: boolean) => void;
  /** When set, this value maps to the visual centre (50%) of the track. */
  midpoint?: number;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);
  const lastTapRef = useRef(0);

  // --- Interaction tracking for popover activation ---
  const hoveredRef = useRef(false);
  const activeRef = useRef(false);
  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;

  const syncActive = useCallback(() => {
    const next = hoveredRef.current || startedRef.current;
    if (next !== activeRef.current) {
      activeRef.current = next;
      onActiveChangeRef.current?.(next);
    }
  }, []);

  const clamp = useCallback(
    (v: number) => {
      const snapped = Math.round(v / step) * step;
      return Math.min(max, Math.max(min, snapped));
    },
    [min, max, step],
  );

  const valueFromPointer = useCallback(
    (clientY: number) => {
      const track = trackRef.current;
      if (!track) return value;
      const rect = track.getBoundingClientRect();
      // Inverted: top = max, bottom = min
      const ratio =
        1 - Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
      return clamp(ratioToValue(ratio, min, max, midpoint));
    },
    [min, max, value, clamp, midpoint],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      startedRef.current = true;
      syncActive();
      onChange(valueFromPointer(e.clientY));
    },
    [valueFromPointer, onChange, syncActive],
  );

  const handleThumbPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (defaultValue === undefined) return;
      const now = Date.now();
      if (now - lastTapRef.current < 350) {
        e.preventDefault();
        e.stopPropagation();
        onChange(defaultValue);
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
    },
    [defaultValue, onChange],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!startedRef.current) return;
      onChange(valueFromPointer(e.clientY));
    },
    [valueFromPointer, onChange],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      startedRef.current = false;
      // After pointer capture release, check if pointer is still over container
      // to keep the popover open seamlessly when transitioning from drag to hover.
      const el = containerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        hoveredRef.current =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;
      }
      syncActive();
    },
    [syncActive],
  );

  const pct = valueToRatio(value, min, max, midpoint) * 100;
  const isAtDefault = value === (defaultValue ?? 0);

  return (
    <div
      ref={containerRef}
      title={tip}
      onPointerEnter={() => {
        hoveredRef.current = true;
        syncActive();
      }}
      onPointerLeave={() => {
        hoveredRef.current = false;
        syncActive();
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      {/* Icon above */}
      <span
        style={{
          color: COLOR_TEXT_FAINT,
          display: "flex",
          alignItems: "center",
        }}
      >
        {icon}
      </span>

      {/* Vertical track */}
      <div
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: "relative",
          width: 28,
          height: V_SLIDER_TRACK_H,
          display: "flex",
          justifyContent: "center",
          cursor: "pointer",
          touchAction: "none",
        }}
      >
        {/* Track background */}
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: 4,
            borderRadius: 2,
            background: COLOR_BORDER,
          }}
        />
        {/* Fill (grows upward from bottom) */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            height: `${pct}%`,
            width: 4,
            borderRadius: 2,
            background: isAtDefault ? COLOR_TEXT_SUBTLE : COLOR_ACCENT,
            opacity: 0.8,
          }}
        />
        {/* Thumb */}
        <div
          onPointerDown={handleThumbPointerDown}
          style={{
            position: "absolute",
            bottom: `${pct}%`,
            width: 14,
            height: 14,
            borderRadius: 7,
            background: isAtDefault ? COLOR_TEXT_SUBTLE : COLOR_ACCENT,
            transform: "translateY(50%)",
            boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
          }}
        />
      </div>
    </div>
  );
}
